import React, { useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import adminTypes from '../../../../constants/adminTypes';
import cn from '../../../../utils/tools/cn';
import PermissionsGate from '../../../../utils/gate/permissionsGate';
import SCOPE from '../../../../utils/gate/scope';

// Icons
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';

// Services
import useAdministratorsService from '../../../../services/administratorsService';
import useRolesService from '../../../../services/rolesService';
import useEstablishmentService from '../../../../services/establishmentService';

// Components
import AdministrationTable from '../../../tables/AdministrationTable/AdministrationTable';
import TabsToggle from '../../../molecules/TabsToggle/TabsToggle';
import SelectField from '../../../atoms/SelectField/SelectField';
import AccountCreation from '../../../tables/AdministrationTable/AccountCreation/AccountCreation';
import Button from '../../../atoms/Button/Button';

import styles from './Administration.module.scss';

function Administration() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [selectedEstablishment, setSelectedEstablishment] = useState(null);

  const [establishmentRoles, setEstablishmentRoles] = useState(null);
  const [platformRoles, setPlatformRoles] = useState(null);
  const [administrators, setAdministrators] = useState(null);
  const [allEstablishments, setAllEstablishments] = useState([]);

  const tabs = [
    { value: adminTypes.platform, label: t('admin.types.platform') },
    { value: adminTypes.establishment, label: t('admin.types.establishment'), disabled: !allEstablishments?.length },
  ];

  const [type, setType] = useState(tabs[0]);

  const { getPlatformRoles, getEstablishmentRoles } = useRolesService();
  const { getPlatformAdministrators, getEstablishmentAdministrators } = useAdministratorsService();
  const { getAllEstablishments } = useEstablishmentService();

  // ROLES
  const { isFetching: isFetchingEstRoles } = useQuery(
    ['getEstablishmentRoles', selectedEstablishment],
    () => getEstablishmentRoles(selectedEstablishment?.value),
    {
      enabled: type?.value === adminTypes.establishment,
      onSuccess: (res) => setEstablishmentRoles(res?.data?.establishmentRoles || []),
    },
  );
  const { isFetching: isFetchingPlatformRoles } = useQuery(
    ['getPlatformRoles'],
    () => getPlatformRoles(),
    {
      enabled: type?.value === adminTypes.platform && !platformRoles,
      onSuccess: (res) => setPlatformRoles(res?.data?.platformRoles || []),
    },
  );

  // ADMIN ACCOUNTS
  const { isFetching: isFetchingEstAdmins } = useQuery(
    ['getEstablishmentAdministrators', establishmentRoles, type],
    () => getEstablishmentAdministrators(selectedEstablishment?.value),
    {
      enabled: !isNil(establishmentRoles) && type?.value === adminTypes.establishment,
      onSuccess: (res) => setAdministrators(res?.data?.accounts || []),
    },
  );
  const { isFetching: isFetchingPlatformAdmins } = useQuery(
    ['getPlatformAdministrators', platformRoles, type],
    () => getPlatformAdministrators(),
    {
      enabled: !isNil(platformRoles) && type?.value === adminTypes.platform,
      onSuccess: (res) => setAdministrators(res?.data?.accounts || []),
    },
  );

  // ESTABLISHMENTS
  const { isFetching: isFetchingEstablishments } = useQuery(
    ['getAllEstablishments'],
    () => getAllEstablishments(),
    {
      onSuccess: (data) => {
        if (data?.data?.establishments?.length) {
          setAllEstablishments(data.data.establishments);
          const first = data.data.establishments[0];
          setSelectedEstablishment({ value: first.id, label: first.name });
        }
      },
    },
  );

  const getRoles = () => {
    switch (type?.value) {
      case adminTypes.platform:
        return platformRoles;
      case adminTypes.establishment:
        return establishmentRoles;
      default:
        return [];
    }
  };

  const isTableFetching = () => (
    isFetchingEstAdmins
    || isFetchingPlatformAdmins
    || isFetchingEstRoles
    || isFetchingPlatformRoles
  );

  return (
    <>
      <div className="d-flex f-wrap m-b-10">
        <div className="m-r-20 m-b-20 mobile-m-r-0 mobile-w-100">
          <TabsToggle
            tabs={tabs}
            setActive={setType}
            active={type}
            className="mobile-w-100"
          />
        </div>

        {type?.value === adminTypes.establishment ? (
          <div className={cn([styles['select-field'], 'd-flex'])}>
            <SelectField
              id="selectedEstablishment"
              name="selectedEstablishment"
              options={allEstablishments?.map((est) => ({ value: est.id, label: est.name }))}
              label={t('admin.table.filters.establishment')}
              disabled={isFetchingEstablishments}
              value={selectedEstablishment}
              onChange={(value) => setSelectedEstablishment(value)}
              clearable={false}
            />
          </div>
        ) : null}

        {!isMobile && type?.value === adminTypes.platform ? (
          <a
            href="/roles"
            className="d-flex a-center c-primary-500 fs-16 font-secondary fw-600 c-pointer m-l-auto m-t-15 p-l-20"
          >
            <EditIcon className="m-r-10" />
            {t('roles.title')}
          </a>
        ) : null}
      </div>

      <AdministrationTable
        tableType={adminTypes[type?.value]}
        roles={getRoles()}
        administrators={administrators}
        setAdministrators={setAdministrators}
        isFetching={isTableFetching()}
      />

      {!isTableFetching() ? (
        <div className="d-flex a-center j-end sticky-actions mobile-a-stretch">
          {isMobile && type?.value === adminTypes.platform ? (
            <a href="/roles" className="m-b-10">
              <Button full theme="inverted-white">
                <EditIcon className="m-r-10" />
                {t('roles.title')}
              </Button>
            </a>
          ) : null}

          <PermissionsGate
            scopes={type?.value === adminTypes.platform
              ? [SCOPE.create_pla_adminAccount]
              : [SCOPE.create_est_adminAccount]}
          >
            <AccountCreation
              tableType={adminTypes[type?.value]}
              roles={getRoles()}
              establishmentId={type?.value === adminTypes.establishment ? selectedEstablishment?.value : null}
              setAdministrators={setAdministrators}
            />
          </PermissionsGate>
        </div>
      ) : null}
    </>
  );
}

Administration.propTypes = {};

export default Administration;
